import { ApiError, createAction } from 'redux-api-middleware';
import { endpointWithVersion } from '../../common/url';
import { State as StoreState } from '../types/State';
import { HTTP_CONTENT_TYPE_HEADER, HTTP_CONTENT_TYPE_JSON, HTTP_GET, HTTP_POST } from './ApiAction';

export const TOS_ACCEPT = 'barmer-bonus-shared/terms-of-service/ACCEPT' as const;
export const TOS_ACCEPT_SUCCESS = 'barmer-bonus-shared/terms-of-service/ACCEPT_SUCCESS' as const;
export const TOS_ACCEPT_FAIL = 'barmer-bonus-shared/terms-of-service/ACCEPT_FAIL' as const;
export const TOS_UPDATE = 'barmer-bonus-shared/terms-of-service/UPDATE' as const;
export const TOS_UPDATE_SUCCESS = 'barmer-bonus-shared/terms-of-service/TOS_UPDATE_SUCCESS' as const;
export const TOS_UPDATE_MANDATORY_SUCCESS = 'barmer-bonus-shared/terms-of-service/TOS_UPDATE_MANDATORY_SUCCESS' as const;
export const TOS_UPDATE_FAIL = 'barmer-bonus-shared/terms-of-service/TOS_UPDATE_FAIL' as const;

type TOSAcceptAction = { type: typeof TOS_ACCEPT; payload?: ApiError; error?: boolean };
type TOSAcceptSuccessAction = {
  type: typeof TOS_ACCEPT_SUCCESS;
  payload: { accepted: Array<TOSItem> };
};
type TOSAcceptFailAction = { type: typeof TOS_ACCEPT_FAIL; payload: ApiError; error: boolean };

type TOSUpdateAction = { type: typeof TOS_UPDATE; payload?: ApiError; error?: boolean };
type TOSUpdateSuccessAction = {
  type: typeof TOS_UPDATE_SUCCESS | typeof TOS_UPDATE_MANDATORY_SUCCESS;
  payload: { [key: string]: TOSItems };
};
type TOSUpdateFailAction = { type: typeof TOS_UPDATE_FAIL; payload: ApiError; error: boolean };

export type Action =
  | TOSUpdateAction
  | TOSAcceptAction
  | TOSAcceptSuccessAction
  | TOSAcceptFailAction
  | TOSUpdateAction
  | TOSUpdateSuccessAction
  | TOSUpdateFailAction;

export const BONUS_PROGRAM = 'bonus' as const;

export type TOSItem = {
  date: string;
  title: string;
  url: string;
  changeSummary: Array<string>;
  program: typeof BONUS_PROGRAM;
};

export type TOSItems = {
  accepted: Array<TOSItem>;
  pending: Array<TOSItem>;
};

export const TOS_ACCEPTANCE_ACCEPTING = 'accepting' as const;
export const TOS_ACCEPTANCE_ACCEPTED = 'accepted' as const;
export const TOS_ACCEPTANCE_ERROR = 'acceptedError' as const;
export const TOS_ACCEPTANCE_UNKNOWN = 'unknown' as const;

type TosAcceptance =
  | typeof TOS_ACCEPTANCE_ACCEPTING
  | typeof TOS_ACCEPTANCE_ACCEPTED
  | typeof TOS_ACCEPTANCE_ERROR
  | typeof TOS_ACCEPTANCE_UNKNOWN;

export const TOS_LOADING = 'loading' as const;
export const TOS_LOADED = 'loaded' as const;
export const TOS_ERROR = 'loadedError' as const;
export const TOS_UNKNOWN = 'unknown' as const;

type TosLoadingState =
  | typeof TOS_LOADING
  | typeof TOS_LOADED
  | typeof TOS_ERROR
  | typeof TOS_UNKNOWN;

export type State = {
  acceptance: TosAcceptance;
  loading: TosLoadingState;
  loadedMandatory: boolean;
  error: ApiError | null | undefined;
  mandatory: {
    accepted: Array<TOSItem>;
    pending: Array<TOSItem>;
  };
  bonus: {
    accepted: Array<TOSItem>;
    pending: Array<TOSItem>;
  };
};

const initialState: State = {
  acceptance: TOS_ACCEPTANCE_UNKNOWN,
  loading: TOS_UNKNOWN,
  loadedMandatory: false,
  error: undefined,
  mandatory: {
    accepted: [],
    pending: [],
  },
  bonus: {
    accepted: [],
    pending: [],
  },
};

/**
 * Find out if there are still mandatory TOS to accept
 * note: only checks the mandatory TOS
 */
export function hasPendingMandatoryTOS(state: StoreState): boolean {
  return state.tos.mandatory.pending.length > 0;
}

/**
 * Find out if the current state is loading TOS Items and/or accepting a TOS Item
 */
export function isLoadingOrAcceptingTOS(state: StoreState): boolean {
  return TOS_LOADING === state.tos.loading || TOS_ACCEPTANCE_ACCEPTING === state.tos.acceptance;
}

/**
 * Returns true when the loadedMandatory === true
 */
export function hasLoadedMandatoryTOS(state: StoreState): boolean {
  return state.tos.loadedMandatory;
}

/**
 * Returns true when the loading state === 'loaded'
 */
export function hasLoadedTOS(state: StoreState): boolean {
  return TOS_LOADED === state.tos.loading;
}

/**
 * This function filters and returns all items from pending when they do not appear in the accepted array.
 * That means if an accepted TOSItem exists in the pending array, it will be filtered out from the pending array -
 * so only the remaining pending items will be returned.
 *
 * @param pending Array with pending TOSItems
 * @param accepted Array with newly/fresh accepted TOSItems
 * @returns original pending items minus the ones that appear in the accepted array
 */
function filterFromPendingWhenExistsInAccepted(
  pending: Array<TOSItem>,
  accepted: Array<TOSItem>,
): Array<TOSItem> {
  return pending.filter((item) => !accepted.includes(item));
}

/**
 * This function concatenates and returns the items that exists in both (accepted and pending) arrays.
 *
 * @param accepted Array with newly/fresh accepted TOSItems
 * @param pending Array with pending TOSItems
 * @param alreadyAccepted Array with previous accepted TOSItems
 * @returns previous accepted + matching pending TOSItems included in accepted TOSItems
 */
function filterAndAppendAcceptedWhenExistedInPending(
  accepted: Array<TOSItem>,
  pending: Array<TOSItem>,
  alreadyAccepted: Array<TOSItem>,
): Array<TOSItem> {
  return [...alreadyAccepted, ...accepted.filter((item) => pending.includes(item))];
}

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case TOS_ACCEPT:
      return {
        ...state,
        acceptance: action.error === undefined ? TOS_ACCEPTANCE_ACCEPTING : TOS_ACCEPTANCE_ERROR,
        error: action.error ? action.payload : undefined,
      };

    case TOS_ACCEPT_SUCCESS:
      return {
        ...state,
        acceptance: TOS_ACCEPTANCE_ACCEPTED,
        mandatory: {
          pending: filterFromPendingWhenExistsInAccepted(
            state.mandatory.pending,
            action.payload.accepted ?? [],
          ),
          accepted: filterAndAppendAcceptedWhenExistedInPending(
            action.payload.accepted ?? [],
            state.mandatory.pending ?? [],
            state.mandatory.accepted ?? [],
          ),
        },
        bonus: {
          pending: filterFromPendingWhenExistsInAccepted(
            state.bonus.pending,
            action.payload.accepted ?? [],
          ),
          accepted: filterAndAppendAcceptedWhenExistedInPending(
            action.payload.accepted ?? [],
            state.bonus.pending ?? [],
            state.bonus.accepted ?? [],
          ),
        },
        error: undefined,
      };

    case TOS_ACCEPT_FAIL:
      return { ...state, acceptance: TOS_ACCEPTANCE_ERROR, error: action.payload };

    case TOS_UPDATE:
      return {
        ...state,
        loading: action.error === undefined ? TOS_LOADING : TOS_ERROR,
        error: action.error ? action.payload : undefined,
      };

    case TOS_UPDATE_SUCCESS:
      return {
        ...state,
        ...action.payload,
        loading: TOS_LOADED,
        acceptance: TOS_ACCEPTANCE_UNKNOWN,
        error: undefined,
      };

    case TOS_UPDATE_MANDATORY_SUCCESS:
      return {
        ...state,
        ...action.payload,
        loadedMandatory: true,
        acceptance: TOS_ACCEPTANCE_UNKNOWN,
        error: undefined,
      };

    case TOS_UPDATE_FAIL:
      return { ...state, loading: TOS_ERROR, error: action.payload };

    default:
      return state;
  }
}

export const acceptTos = (accepted: Array<TOSItem>) =>
  createAction({
    endpoint: '/terms/accepted',
    types: [
      TOS_ACCEPT,
      {
        type: TOS_ACCEPT_SUCCESS,
        payload: { accepted }, // Pass back the accepted items on success
      },
      TOS_ACCEPT_FAIL,
    ],
    method: HTTP_POST,
    headers: { [HTTP_CONTENT_TYPE_HEADER]: HTTP_CONTENT_TYPE_JSON },
    body: JSON.stringify({
      accepted: accepted.map((e: TOSItem) => ({
        url: e.url,
      })),
    }),
  });

export const loadTos = (platform: string, version?: string) =>
  createAction({
    endpoint: endpointWithVersion('/terms', platform, version),
    types: [TOS_UPDATE, TOS_UPDATE_SUCCESS, TOS_UPDATE_FAIL],
    method: HTTP_GET,
  });

export const updateMandatoryTos = (update: TOSItems): Action => {
  return { type: TOS_UPDATE_MANDATORY_SUCCESS, payload: { mandatory: update } };
};
